import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import { useQuery } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { track } from '@/lib/track'
import { cn } from '@/lib/utils'
import { Check, Link2, FastForward, RotateCcw, ArrowRight, Sparkles, Loader2 } from 'lucide-react'
import {
  MessageBubble,
  TimestampSeparator,
  ToolCallsSection,
  HIDDEN_TOOL_CALLS,
} from '@/components/workflow/WorkflowChat'
import { PreviewPlayer, ClipListWithSeek } from '@/routes/video/[id]'
import { VideoSeekProvider } from '@/contexts/video-seek-context'
import {
  convertWorkflowMessages,
  computeAggregatedToolCalls,
  computeLatestTreatmentToolId,
  computeMessageTerminalStatus,
  findExistingAnswer,
  parseAgentErrorContent,
  type ChatMessage,
} from '@/lib/chatMessages'
import { LOGO_URL, OG_IMAGE_URL, siteUrl } from '@/lib/config/site'

// Replay pacing: real conversation gaps compressed 4x, clamped so dead air
// (e.g. minutes of video generation) never stalls the playback.
const REPLAY_SPEED = 4
const MIN_STEP_MS = 260
const MAX_STEP_MS = 4200
// Live chat reveals at 3 chars/frame; replay types 4x faster.
const TYPE_CHARS_PER_FRAME = 12
const TIMESTAMP_GAP_MS = 5 * 60 * 1000

function stepDelay(prev: ChatMessage | null, next: ChatMessage): number {
  if (!prev?.timestamp || !next.timestamp) return 600
  const gap = (next.timestamp - prev.timestamp) / REPLAY_SPEED
  return Math.max(MIN_STEP_MS, Math.min(MAX_STEP_MS, gap))
}

function ThinkingDots() {
  return (
    <div className="flex items-start animate-in fade-in duration-300">
      <div className="inline-flex items-center gap-1 rounded-xl px-3.5 py-2.5 bg-secondary border border-border/50">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce"
            style={{ animationDelay: `${i * 150}ms`, animationDuration: '900ms' }}
          />
        ))}
      </div>
    </div>
  )
}

export default function SharedConversationPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const data = useQuery(api.workflows.getSharedConversation, id ? { shortcode: id } : 'skip')

  const initialMode = searchParams.get('view') === 'full' ? 'full' : 'replay'
  const [mode, setMode] = useState<'replay' | 'full'>(initialMode)
  const [visibleCount, setVisibleCount] = useState(0)
  const [typingIndex, setTypingIndex] = useState<number | null>(null)
  const [copied, setCopied] = useState(false)
  const [selectedClipId, setSelectedClipId] = useState<string | null>(null)
  const timerRef = useRef<number | null>(null)
  const autoScrollRef = useRef(true)
  const lastScrollYRef = useRef(0)
  const viewTracked = useRef(false)

  const messages = useMemo(() => {
    if (!data) return null
    // isStreaming forced off: the replay engine drives its own typewriter
    return convertWorkflowMessages(data.messages).map((m) => ({ ...m, isStreaming: false }))
  }, [data])

  const total = messages?.length ?? 0
  const done = mode === 'full' || (total > 0 && visibleCount >= total) || total === 0

  useEffect(() => {
    if (data && id && !viewTracked.current) {
      viewTracked.current = true
      track('share.conversation_viewed', { shortcode: id, mode: initialMode })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, id])

  // ── Replay engine ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (mode !== 'replay' || !messages) return
    if (typingIndex !== null || visibleCount >= messages.length) return

    const next = messages[visibleCount]
    const prev = visibleCount > 0 ? messages[visibleCount - 1] : null
    timerRef.current = window.setTimeout(() => {
      const { visibleContent } = parseAgentErrorContent(next.content)
      if (next.role === 'assistant' && visibleContent.trim()) {
        setTypingIndex(visibleCount)
      } else {
        setVisibleCount((c) => c + 1)
      }
    }, stepDelay(prev, next))

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
    }
  }, [mode, messages, visibleCount, typingIndex])

  const replayGenRef = useRef(0)

  const handleTypingComplete = useCallback(() => {
    const gen = replayGenRef.current
    // small beat after the text lands before tool pills / widgets pop in
    window.setTimeout(() => {
      if (replayGenRef.current !== gen) return // skipped or restarted meanwhile
      setTypingIndex(null)
      setVisibleCount((c) => c + 1)
    }, 180)
  }, [])

  const typewriter = useMemo(
    () => ({ charsPerFrame: TYPE_CHARS_PER_FRAME, onComplete: handleTypingComplete }),
    [handleTypingComplete]
  )

  const skipToFull = useCallback(() => {
    replayGenRef.current++
    if (timerRef.current) window.clearTimeout(timerRef.current)
    setTypingIndex(null)
    setMode('full')
    setVisibleCount(total)
    window.scrollTo({ top: 0 })
  }, [total])

  const restartReplay = useCallback(() => {
    replayGenRef.current++
    setVisibleCount(0)
    setTypingIndex(null)
    autoScrollRef.current = true
    setMode('replay')
    window.scrollTo({ top: 0 })
  }, [])

  // ── Auto-scroll: follow the conversation unless the viewer scrolls up ────
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY
      const nearBottom = window.innerHeight + y >= document.documentElement.scrollHeight - 160
      if (y < lastScrollYRef.current - 2 && !nearBottom) autoScrollRef.current = false
      if (nearBottom) autoScrollRef.current = true
      lastScrollYRef.current = y
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (mode !== 'replay' || !autoScrollRef.current) return
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })
  }, [mode, visibleCount, typingIndex])

  // Keep pinned to bottom while the typewriter grows the page
  useEffect(() => {
    if (mode !== 'replay' || typingIndex === null) return
    const interval = window.setInterval(() => {
      if (autoScrollRef.current) {
        window.scrollTo({ top: document.documentElement.scrollHeight })
      }
    }, 250)
    return () => window.clearInterval(interval)
  }, [mode, typingIndex])

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/share/chat/${id}`)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard unavailable */
    }
  }, [id])

  // ── Derived render data (same pipeline as the live chat) ────────────────
  const shownMessages = useMemo(() => {
    if (!messages) return []
    return mode === 'full' ? messages : messages.slice(0, visibleCount)
  }, [messages, mode, visibleCount])

  // Aggregation over the visible slice so tool pills accumulate like they did live
  const aggregated = useMemo(() => computeAggregatedToolCalls(shownMessages), [shownMessages])
  // Latest treatment board so far — earlier boards render as superseded, like live
  const latestTreatmentToolId = useMemo(() => computeLatestTreatmentToolId(shownMessages), [shownMessages])
  // Terminal status over the full list so "generating" pills resolve to Done/Failed
  const terminalStatus = useMemo(
    () =>
      messages
        ? computeMessageTerminalStatus(messages)
        : new Map<string, 'ready' | 'cancelled' | 'failed'>(),
    [messages]
  )

  const typingMessage = typingIndex !== null && messages ? messages[typingIndex] : null
  const waitingForAssistant =
    mode === 'replay' &&
    !done &&
    typingIndex === null &&
    messages?.[visibleCount]?.role === 'assistant'

  // ── Preview: the SAME PreviewPlayer + clip list the project editor uses ──
  const clips = useMemo(() => data?.clips ?? [], [data])
  const hasPreview = clips.length > 0

  // ── Loading / not found ──────────────────────────────────────────────────
  if (data === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafafa]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
          <span className="text-sm text-zinc-400">Loading conversation...</span>
        </div>
      </div>
    )
  }

  if (data === null) {
    return (
      <div className="min-h-screen flex flex-col bg-[#fafafa]">
        <header className="px-4 sm:px-6 py-4">
          <Link to="/">
            <img src={LOGO_URL} alt="Footage" className="h-8 w-8" />
          </Link>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center gap-5 px-4 text-center">
          <h1 className="text-xl font-semibold text-zinc-900">This conversation isn't shared</h1>
          <p className="text-sm text-zinc-500 max-w-sm">
            The creator may have turned off sharing, or the link is incorrect.
          </p>
          <Link
            to="/"
            className="mt-1 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-full text-zinc-700 bg-zinc-200 hover:bg-zinc-300 transition-colors"
          >
            Go to Footage
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    )
  }

  const { workflow } = data
  const title = workflow.title || 'Untitled project'
  const aspectRatio = workflow.settings?.aspectRatio ?? '16:9'
  const pageTitle = `Watch how "${title}" was made — Footage`
  const canonicalUrl = siteUrl(`/share/chat/${id}`)
  const progress = total > 0 ? Math.min(100, ((visibleCount + (typingIndex !== null ? 0.5 : 0)) / total) * 100) : 100

  return (
    <div className="min-h-screen bg-[#fafafa] text-zinc-900">
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={`Replay the full creative conversation behind "${title}" — every direction, decision, and generation, made with Footage.`}
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta
          property="og:description"
          content={`Replay the full creative conversation behind "${title}".`}
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={workflow.thumbnailUrl || OG_IMAGE_URL} />
      </Helmet>

      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-zinc-200/70">
        <div className="max-w-[1180px] mx-auto px-4 h-14 flex items-center gap-3">
          <Link to="/" className="shrink-0">
            <img src={LOGO_URL} alt="Footage" className="h-7 w-7" />
          </Link>
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-medium leading-tight truncate">{title}</div>
            <div className="text-[11px] text-zinc-400 leading-tight">
              {done ? 'Full conversation' : 'Replaying the conversation'}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!done && (
              <>
                <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-semibold text-zinc-500 px-2 py-1 rounded-full bg-zinc-100">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  4× replay
                </span>
                <button
                  onClick={skipToFull}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full text-zinc-600 bg-zinc-100 hover:bg-zinc-200 transition-colors"
                >
                  <FastForward className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Skip to full conversation</span>
                  <span className="sm:hidden">Skip</span>
                </button>
              </>
            )}
            {done && total > 0 && (
              <button
                onClick={restartReplay}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full text-zinc-600 bg-zinc-100 hover:bg-zinc-200 transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Watch replay</span>
                <span className="sm:hidden">Replay</span>
              </button>
            )}
            <button
              onClick={handleCopyLink}
              title="Copy link"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-full text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
        {/* Replay progress */}
        {!done && (
          <div className="h-0.5 bg-zinc-200/60">
            <div
              className="h-full bg-zinc-900 transition-[width] duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </header>

      {/* Conversation + preview */}
      <div className="max-w-[1180px] mx-auto w-full px-3 sm:px-5 flex flex-col lg:flex-row lg:gap-6">
        {hasPreview && (
          <aside
            className={cn(
              'order-1 lg:order-2 lg:w-[400px] xl:w-[440px] lg:shrink-0',
              'sticky top-14 z-20 lg:self-start lg:h-[calc(100vh-3.5rem)]',
              'bg-[#fafafa]/95 backdrop-blur lg:bg-transparent',
              'border-b lg:border-b-0 border-zinc-200/70',
              'py-3 lg:py-5'
            )}
          >
            <VideoSeekProvider>
              <div className="flex flex-col gap-4 lg:h-full lg:min-h-0">
                <PreviewPlayer
                  clips={clips as never}
                  selectedClipId={selectedClipId}
                  onSelectClip={setSelectedClipId}
                  aspectRatio={aspectRatio}
                  workflow={workflow}
                  workflowId={null}
                />
                {/* Clip list — same rows as the editor; hidden on mobile to keep
                    the sticky preview compact. */}
                <div className="hidden lg:block flex-1 min-h-0 overflow-y-auto">
                  <ClipListWithSeek
                    clips={clips as never}
                    characters={workflow.characters as never}
                    timelineMode={workflow.timelineMode}
                    selectedId={selectedClipId}
                    onSelect={setSelectedClipId}
                  />
                </div>
              </div>
            </VideoSeekProvider>
          </aside>
        )}

        <main className="order-2 lg:order-1 flex-1 min-w-0 pt-6 pb-16">
          <div className="mx-auto w-full max-w-[680px] space-y-3">
            {total === 0 && (
              <p className="text-center text-sm text-zinc-400 py-12">This conversation is empty.</p>
            )}

            {shownMessages.map((message, index) => {
              const aggregatedTools = aggregated.groups.get(index)
              const skipToolCalls =
                !!aggregatedTools || message.toolCalls?.some((tc) => aggregated.seenToolIds.has(tc.id))

              const filteredAggregatedTools = aggregatedTools?.filter((tc) => !HIDDEN_TOOL_CALLS.has(tc.name))
              const hasVisibleAggregatedTools = filteredAggregatedTools && filteredAggregatedTools.length > 0

              // Skip selection messages from question pickers (zero-width-space prefix)
              if (message.role === 'user' && message.content?.startsWith('\u200B')) return null

              // Skip assistant messages that would render nothing visible
              if (message.role === 'assistant') {
                const hasContent = message.content?.trim() || message.contentAfterTools?.trim()
                const hasSpecialWidget = message.toolCalls?.some(
                  (tc) =>
                    tc.status === 'completed' &&
                    tc.result?.success &&
                    ['question_options', 'question_sequence', 'media_grid', 'media_clusters', 'folder_preview', 'treatment_board'].includes(
                      (tc.result?.data as { type?: string })?.type || ''
                    )
                )
                if (!hasContent && !hasVisibleAggregatedTools && !hasSpecialWidget) return null
              }

              const prevMessage = index > 0 ? shownMessages[index - 1] : null
              const showTimestampSeparator =
                message.timestamp &&
                (!prevMessage?.timestamp || message.timestamp - prevMessage.timestamp > TIMESTAMP_GAP_MS)

              return (
                <div
                  key={message.id}
                  data-message-role={message.role}
                  className={cn(mode === 'replay' && 'animate-in fade-in slide-in-from-bottom-2 duration-300')}
                >
                  {showTimestampSeparator && <TimestampSeparator timestamp={message.timestamp!} />}
                  <MessageBubble
                    message={message}
                    messageTerminalStatus={terminalStatus}
                    skipToolCalls={skipToolCalls}
                    workflowId={null}
                    existingAnswer={findExistingAnswer(shownMessages, index)}
                    latestTreatmentToolId={latestTreatmentToolId}
                  />
                  {hasVisibleAggregatedTools && (
                    <div className="max-w-[85%] mt-1">
                      <ToolCallsSection tools={filteredAggregatedTools!} />
                    </div>
                  )}
                </div>
              )
            })}

            {/* Currently typing assistant message */}
            {typingMessage && (
              <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                <MessageBubble
                  message={{ ...typingMessage, isStreaming: true, toolCalls: undefined, suggestedActions: undefined }}
                  workflowId={null}
                  typewriter={typewriter}
                />
              </div>
            )}

            {/* Agent thinking indicator between messages */}
            {waitingForAssistant && !typingMessage && <ThinkingDots />}

            {/* End card */}
            {done && (
              <div className="pt-10 animate-in fade-in slide-in-from-bottom-3 duration-500">
                <div className="rounded-2xl bg-linear-to-br from-zinc-100 to-zinc-50 ring-1 ring-zinc-200 p-6 sm:p-7">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Sparkles className="w-5 h-5 text-teal-600 shrink-0" />
                        <span className="text-lg font-bold text-zinc-900">Made with Footage</span>
                      </div>
                      <p className="text-sm leading-relaxed text-zinc-500">
                        This whole video was directed in a conversation. Yours can be too.
                      </p>
                    </div>
                    <Link
                      to="/"
                      className="inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-linear-to-r from-teal-600 to-cyan-500 rounded-full hover:from-teal-700 hover:to-cyan-600 transition-all shrink-0 whitespace-nowrap"
                    >
                      Start creating free
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}
